import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeDollarSign,
  Compass,
  Handshake,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import SectionHeader from "@/components/sections/SectionHeader";
import SplitSection from "@/components/sections/SplitSection";
import { usePageContent } from "@/hooks/usePageContent";
import heroImage from "@/assets/dr-investing-hero.jpg";
import aboutCaribbean from "@/assets/about-caribbean.jpg";
import drListings from "@/data/drListings";

const habitatBeachGarden = drListings.find(
  (listing) => listing.name === "Habitat Beach Garden"
);
const habitatBeachTower = drListings.find(
  (listing) => listing.name === "Habitat Beach Tower"
);
const traviesoCabarete = drListings.find(
  (listing) => listing.name === "Travieso Cabarete – Beach Apartments"
);
const godViewCabrera = drListings.find(
  (listing) => listing.name === "God View – Oceanfront Apartments"
);

const marketDrivers = [
  {
    eyebrow: "Demand",
    title: "Strong and consistent tourism",
    description:
      "The Dominican Republic continues to attract global attention, supporting demand for lifestyle-driven and rental-ready property.",
    icon: Sparkles,
    index: "01",
  },
  {
    eyebrow: "Entry Point",
    title: "Lower entry prices with real upside",
    description:
      "Compared to many U.S. markets, buyers can access attractive assets at a lower entry point while still pursuing long-term value.",
    icon: BadgeDollarSign,
    index: "02",
  },
  {
    eyebrow: "Momentum",
    title: "Lifestyle appeal plus investment potential",
    description:
      "The opportunity is not only about ownership. It is about selecting a property that performs over time and aligns with how buyers want to use it.",
    icon: TrendingUp,
    index: "03",
  },
];

const investmentPaths = [
  {
    eyebrow: "Path 01",
    title: "Second home ownership",
    description:
      "A personal escape that still benefits from being in a market with growing demand and long-term interest.",
    image: habitatBeachGarden?.images[0] ?? aboutCaribbean,
    imageAlt: "Dominican Republic second home and lifestyle ownership opportunity",
  },
  {
    eyebrow: "Path 02",
    title: "Vacation rental strategy",
    description:
      "Properties positioned in high-interest locations where tourism and short-term rental demand create income potential.",
    image: habitatBeachTower?.images[3] ?? traviesoCabarete?.images[0] ?? habitatBeachGarden?.images[1] ?? aboutCaribbean,
    imageAlt: "Vacation rental investment opportunity in the Dominican Republic",
  },
  {
    eyebrow: "Path 03",
    title: "Long-term investment",
    description:
      "Assets selected for appreciation, location strength, and the ability to build value over time.",
    image: habitatBeachTower?.images[0] ?? godViewCabrera?.images[0] ?? aboutCaribbean,
    imageAlt: "Long-term real estate investment opportunity in the Dominican Republic",
  },
  {
    eyebrow: "Path 04",
    title: "Lifestyle-led relocation",
    description:
      "Properties that support a personal move while still being evaluated through a disciplined investment lens.",
    image: godViewCabrera?.images[0] ?? habitatBeachGarden?.images[2] ?? aboutCaribbean,
    imageAlt: "Lifestyle and relocation property opportunity in the Dominican Republic",
  },
];

const riskPoints = [
  {
    title: "Look great but underperform",
    detail:
      "Appearance alone does not tell you whether a property can generate income or hold value over time.",
  },
  {
    title: "Are in low-demand areas",
    detail:
      "Location quality matters because demand, occupancy, and long-term appeal vary dramatically market to market.",
  },
  {
    title: "Have poor rental potential",
    detail:
      "A property can feel exciting in person and still miss the factors that make short-term or long-term returns work.",
  },
];


const differentiators = [
  {
    eyebrow: "Guidance",
    title: "Investment lens before emotional decisions",
    description:
      "We help you evaluate opportunities through an investment lens and make decisions based on strategy, not emotion.",
    icon: Compass,
    index: "01",
  },
  {
    eyebrow: "Protection",
    title: "Risk awareness before you commit",
    description:
      "We help you understand risks before you commit and navigate an international purchase with confidence.",
    icon: ShieldCheck,
    index: "02",
  },
  {
    eyebrow: "Partnership",
    title: "Relationship-first international guidance",
    description:
      "This is the difference between owning property and building value with a guide who understands both opportunity and execution.",
    icon: Handshake,
    index: "03",
  },
];

const rightFit = [
  {
    lead: "Buyers looking for short-term rental income",
    detail: "This market can appeal to buyers who want a lifestyle asset with clear earning potential.",
  },
  {
    lead: "Investors seeking growth in emerging markets",
    detail: "It can also suit those who want exposure to a market with momentum, demand, and room to appreciate.",
  },
  {
    lead: "Individuals interested in a second home with upside",
    detail: "Some buyers are balancing personal use with long-term value and want a strategy that supports both.",
  },
  {
    lead: "Those open to international real estate",
    detail: "This is best for people who want guidance before making a cross-border decision, not after.",
  },
];

const DominicanRepublicInvesting = () => {
  const { getContent } = usePageContent({ pageKey: "dr_investing" });
  useEffect(() => {
    document.title = "Dominican Republic Real Estate Investing | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Explore Dominican Republic real estate investing with guidance on second homes, vacation rental opportunities, beachfront property selection, and cross-border buyer strategy."
      );
    }
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://hadassahhomesinc.com/dominican-republic";
  }, []);

  return (
    <SiteLayout>
      <PageHero
        title={getContent("hero", "title", "Dominican Republic Real Estate Investing with Strategy")}
        subtitle={getContent("hero", "subtitle", "Explore second homes, vacation rental opportunities, and destination-driven property investments with a clearer cross-border plan.")}
        backgroundImage={heroImage}
      >
        <div className="mx-auto mt-2 max-w-2xl space-y-6 md:space-y-7">
          <p className="mx-auto max-w-xl text-cream/90 text-lg md:text-[1.35rem] leading-[1.7]">
            We help international buyers evaluate Dominican Republic opportunities through rental demand, tourism-driven interest, lifestyle fit, and long-term value potential.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/consultation"
              className="inline-flex min-w-[260px] items-center justify-center bg-accent px-8 py-4 font-body text-sm uppercase tracking-[0.12em] text-accent-foreground transition-colors hover:bg-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
            >
              Book an Investment Consultation
            </Link>
            <Link
              to="/dominican-republic-investing/properties"
              className="inline-flex min-w-[260px] items-center justify-center border border-cream/50 px-8 py-4 font-body text-sm uppercase tracking-[0.12em] text-cream transition-colors hover:border-cream hover:bg-cream/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cream/60 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
            >
              Explore Dominican Republic Properties
            </Link>
          </div>
        </div>
      </PageHero>

      <ContentSection>
        <SplitSection
          title={getContent(
            "intro",
            "heading",
            "The Dominican Republic is no longer a hidden opportunity.",
          )}
          description={getContent(
            "intro",
            "body",
            "It is one of the fastest-growing real estate markets in the Caribbean, driven by tourism, development, and increasing global interest. But the opportunity is not just in buying property. It is in choosing the right location, the right use case, and the right strategy for income, lifestyle, and long-term value.",
          )}
          image={aboutCaribbean}
          imageAlt="Dominican Republic destination and lifestyle investment scene"
        >
          <p className="text-muted-foreground text-base md:text-lg leading-[1.8]">
            At Hadassah Homes, we help you approach this market with clarity, structure, and guidance designed for cross-border buyers making strategic real estate decisions.
          </p>
          <div className="pt-2">
            <Link
              to="/consultation"
              className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.12em] text-accent hover:text-accent/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              Start exploring your investment options
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </SplitSection>
      </ContentSection>

      <section className="relative overflow-hidden border-y border-[#C9A95C]/15 bg-[#173D2D] py-24 md:py-32">
        <div className="pointer-events-none absolute -right-20 top-10 h-72 w-72 bg-[radial-gradient(circle,rgba(201,169,92,0.14)_0%,rgba(201,169,92,0.05)_34%,transparent_74%)]" />
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 lg:px-14">
          <div className="mx-auto mb-16 max-w-4xl space-y-5 text-center md:mb-20">
            <p className="font-body text-xs font-medium uppercase tracking-[0.22em] text-[#C9A95C]">
              Market Momentum
            </p>
            <h2 className="font-headline text-4xl leading-tight tracking-tight text-white md:text-5xl">
              Why Investors Are Paying Attention Now
            </h2>
            <p className="mx-auto max-w-2xl leading-relaxed text-white/80 md:text-lg">
              This market is growing for a reason.
            </p>
            <p className="mx-auto max-w-2xl leading-relaxed text-white/80 md:text-lg">
              For the right buyer, this creates real opportunity. But timing and selection matter.
            </p>
            <p className="mx-auto max-w-2xl text-sm font-medium leading-relaxed text-[#C9A95C]">
              The opportunity is real, but the strategy matters more than the hype.
            </p>
          </div>

          <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-3 lg:gap-8">
            {marketDrivers.map((item) => (
              <article
                key={item.title}
                className="h-full border border-white/15 bg-white/[0.055] p-6 text-white transition-colors duration-300 hover:bg-white/[0.08] md:p-8"
              >
                <div className="space-y-5">
                  <div className="flex items-start justify-between gap-6 border-t border-[#C9A95C]/45 pt-4">
                    <span className="font-body text-xs uppercase tracking-[0.18em] text-[#C9A95C]">
                      {item.eyebrow}
                    </span>
                    <span className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A95C]">
                      {item.index}
                    </span>
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-headline text-xl leading-tight text-white md:text-2xl">
                      {item.title}
                    </h3>
                    <p className="max-w-3xl text-base leading-relaxed text-white/78 md:text-lg">
                      {item.description}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <ContentSection variant="dark" className="py-28 md:py-36">
        <div className="max-w-[54rem] mx-auto text-center space-y-7 md:space-y-9">
          <span className="font-body text-xs uppercase tracking-[0.14em] text-accent">
            Strategy
          </span>
          <h2 className="font-headline text-4xl md:text-[3.65rem] lg:text-[4.5rem] text-secondary-foreground leading-[1.02]">
            This Is Not Just a Purchase. It Is a Strategy.
          </h2>
          <p className="text-secondary-foreground/86 text-base md:text-lg leading-[1.9] max-w-3xl mx-auto">
            Many buyers enter this market emotionally. They see a beach. A view. A lifestyle. But strong investors look deeper: rental demand, occupancy, growth indicators, and how the property will perform over time. We help you answer these questions before you commit.
          </p>
        </div>
      </ContentSection>

      <ContentSection>
        <SectionHeader
          title="Investment Paths We Help You Navigate"
          description="Every investor has a different goal. We help you choose the right approach."
        />
        <div className="grid grid-cols-1 gap-6 py-20 md:grid-cols-2 md:gap-8 md:py-28">
          {investmentPaths.map((item) => (
            <article
              key={item.title}
              className="group overflow-hidden bg-secondary-foreground/[0.03] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_64px_-34px_rgba(0,0,0,0.5)]"
            >
              <div className="relative aspect-[5/4] overflow-hidden">
                <img
                  src={item.image}
                  alt={item.imageAlt}
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/10" />
                <span className="absolute left-5 top-5 z-10 inline-flex bg-accent px-3 py-1.5 font-body text-[10px] uppercase tracking-[0.18em] text-accent-foreground">
                  {item.eyebrow}
                </span>
                <div className="absolute inset-x-0 bottom-0 z-10 space-y-3 px-6 pb-6 pt-16 md:px-7 md:pb-7 md:pt-20">
                  <h3 className="font-headline text-[1.85rem] leading-[1.08] text-white [text-shadow:0_2px_18px_rgba(0,0,0,0.35)] md:text-[2rem]">
                    {item.title}
                  </h3>
                  <p className="max-w-[30rem] text-base leading-[1.7] text-white/90 [text-shadow:0_1px_10px_rgba(0,0,0,0.28)]">
                    {item.description}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </ContentSection>

      <ContentSection>
        <div className="grid grid-cols-1 lg:grid-cols-[0.44fr_0.56fr] gap-12 md:gap-16 lg:gap-20 items-start">
          <div className="space-y-6 md:space-y-7">
            <SectionHeader
              title="Where Most People Get It Wrong"
              align="left"
            />
            <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-[1.8]">
              <p>The biggest mistake is assuming all properties perform the same.</p>
              <p>They don&apos;t.</p>
              <p>We help you avoid these mistakes and focus on opportunities that align with your goals.</p>
            </div>
          </div>

          <div className="space-y-8 md:space-y-10 border-l border-accent/22 pl-6 md:pl-8">
            {riskPoints.map((item, index) => (
              <div
                key={item.title}
                className={[
                  "space-y-4 pb-8 md:pb-10",
                  index < riskPoints.length - 1 ? "border-b border-border/55" : "",
                ].join(" ")}
              >
                <p className="font-body text-[10px] uppercase tracking-[0.18em] text-accent">
                  Risk Point {String(index + 1).padStart(2, "0")}
                </p>
                <p className="font-headline text-[1.75rem] md:text-[2rem] text-foreground leading-[1.2]">
                  {item.title}
                </p>
                <p className="max-w-xl text-muted-foreground text-base leading-[1.8]">
                  {item.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </ContentSection>

      <ContentSection variant="dark" id="investment-opportunities">
        <SectionHeader
          title="Featured Investment Opportunities"
          description="We highlight select properties with strong income potential and long-term upside."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
          {drListings.slice(0, 3).map((listing) => (
            <Link
              key={listing.slug}
              to={`/dominican-republic-investing/properties/${listing.slug}`}
              className="group overflow-hidden bg-secondary-foreground/[0.03] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_64px_-34px_rgba(0,0,0,0.5)]"
            >
              <div className="relative aspect-[5/4] overflow-hidden">
                <img
                  src={listing.images[0]}
                  alt={listing.name}
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                  loading="lazy"
                />
                <span className="absolute left-5 top-5 inline-flex bg-accent px-3 py-1.5 font-body text-[10px] uppercase tracking-[0.18em] text-accent-foreground">
                  {listing.tag}
                </span>
              </div>
              <div className="space-y-4 px-6 py-6 md:px-7 md:py-7">
                <span className="inline-flex border border-accent/35 px-3 py-1 font-body text-[10px] uppercase tracking-[0.18em] text-accent">
                  {listing.type}
                </span>
                <h3 className="font-headline text-[1.85rem] md:text-[2rem] text-secondary-foreground leading-[1.12]">
                  {listing.name}
                </h3>
                <p className="font-body text-sm uppercase tracking-[0.14em] text-accent/80 mb-1">
                  {listing.location}
                </p>
                <p className="text-secondary-foreground/82 text-base leading-[1.75] line-clamp-3">
                  {listing.description[0]}
                </p>
                <p className="font-body text-sm font-semibold text-secondary-foreground mt-2">
                  {listing.price}
                  {listing.priceNote && (
                    <span className="ml-2 text-xs font-normal text-secondary-foreground/50">{listing.priceNote}</span>
                  )}
                </p>
              </div>
            </Link>
          ))}
        </div>
        <div className="max-w-3xl mx-auto mt-12 md:mt-14 text-center space-y-4">
          <p className="font-headline text-xl md:text-2xl text-secondary-foreground">
            Request pricing, availability, and ROI details
          </p>
          <Link
            to="/dominican-republic-investing/properties"
            className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.12em] text-accent hover:text-accent/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
          >
            View All DR Listings
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </ContentSection>

      <ContentSection>
        <div className="grid grid-cols-1 lg:grid-cols-[0.42fr_0.58fr] gap-12 md:gap-16 lg:gap-20 items-start">
          <div className="space-y-6 md:space-y-7">
            <SectionHeader
              title="What Makes Hadassah Homes Different"
              align="left"
            />
            <p className="font-body text-[10px] uppercase tracking-[0.18em] text-accent">
              Guiding international buyers with clarity, discipline, and cross-border confidence.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-[1.8]">
              We are not just connecting you to properties. We help you evaluate Dominican Republic opportunities through an investment lens, understand risks before you commit, navigate a cross-border purchase with confidence, and make decisions based on strategy instead of hype.
            </p>
            <div className="border-l border-accent/55 pl-5 md:pl-6">
              <p className="font-headline text-2xl md:text-[2rem] text-foreground leading-[1.25]">
                This is the difference between simply buying abroad and building value with intention.
              </p>
            </div>
          </div>

          <div className="space-y-10 md:space-y-12">
            {differentiators.map((item, index) => (
              <div
                key={item.title}
                className={index < differentiators.length - 1 ? "border-b border-border/55 pb-10 md:pb-12" : ""}
              >
                <div className="space-y-5">
                  <div className="flex items-center justify-between gap-5">
                    <div className="flex items-center gap-4">
                      <span className="font-body text-[10px] uppercase tracking-[0.18em] text-accent">
                        {item.eyebrow}
                      </span>
                      <span className="font-headline text-3xl md:text-4xl leading-none text-accent/22">
                        {item.index}
                      </span>
                    </div>
                    <item.icon className="h-5 w-5 text-accent" strokeWidth={1.7} aria-hidden="true" />
                  </div>
                  <h3 className="font-headline text-[2rem] md:text-[2.4rem] text-foreground leading-[1.08]">
                    {item.title}
                  </h3>
                  <p className="max-w-2xl text-muted-foreground text-base md:text-lg leading-[1.8]">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <div className="grid grid-cols-1 lg:grid-cols-[0.48fr_0.52fr] gap-12 md:gap-16 lg:gap-20 items-start">
          <div className="space-y-6 md:space-y-7">
            <SectionHeader
              title="Is This Right for You"
              align="left"
            />
            <p className="text-secondary-foreground/82 text-base md:text-lg leading-[1.85]">
              This opportunity is best suited for buyers who want to understand whether Dominican Republic real estate aligns with their goals before they move forward.
            </p>
            <p className="text-secondary-foreground/82 text-base md:text-lg leading-[1.85]">
              We help you evaluate lifestyle fit, rental potential, market selection, and execution before you commit.
            </p>
          </div>

          <div className="space-y-7 md:space-y-8 border-l border-accent/22 pl-6 md:pl-8">
            {rightFit.map((item, index) => (
              <div
                key={item.lead}
                className={index < rightFit.length - 1 ? "border-b border-secondary-foreground/10 pb-7 md:pb-8" : ""}
              >
                <p className="font-headline text-[1.45rem] md:text-[1.85rem] text-secondary-foreground leading-[1.22]">
                  {item.lead}
                </p>
                <p className="mt-3 text-secondary-foreground/78 text-base md:text-[1.02rem] leading-[1.8]">
                  {item.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </ContentSection>

      <section className="relative overflow-hidden py-24 md:py-32">
        <img
          src={heroImage}
          alt="Dominican Republic destination investment consultation"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/75 via-black/45 to-black/20" />
        <div className="absolute inset-0 z-10 bg-gradient-to-r from-black/92 via-black/70 to-black/44" />

        <div className="relative z-20 mx-auto max-w-content px-5 md:px-12">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl leading-tight text-white mb-5">
              {getContent("final_cta", "heading", "Let's Explore the Opportunity the Right Way")}
            </h2>
            <p className="text-white/90 text-base md:text-lg leading-[1.8] max-w-2xl mx-auto mb-10">
              {getContent(
                "final_cta",
                "body",
                "The Dominican Republic offers real potential. But the outcome depends on how you approach it. We will help you understand your options, evaluate opportunities, and move forward with confidence.",
              )}
            </p>
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                to={getContent("final_cta", "button_url", "/consultation")}
                className="inline-flex min-w-[250px] items-center justify-center font-body text-sm uppercase tracking-[0.12em] px-10 py-4 bg-accent text-accent-foreground hover:bg-accent/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
              >
                {getContent("final_cta", "button_label", "Book a Consultation")}
              </Link>
              <Link
                to="/dominican-republic-investing/properties"
                className="inline-flex min-w-[250px] items-center justify-center border border-white/40 px-10 py-4 font-body text-sm uppercase tracking-[0.12em] text-white transition-colors hover:bg-white/10 hover:border-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
              >
                Explore Dominican Republic Properties
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default DominicanRepublicInvesting;
